import {
  ThumbDownAlt as ThumbDownIcon,
  ThumbUpAlt as ThumbUpIcon,
} from "@mui/icons-material/";
import {
  Autocomplete,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import CommonTextField from "../../common/components/functional/commonTextField/CommonTextField";
import DialogComponent from "../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import { leadingZerosDateFormat } from "../../common/helpers/formatDateTime";
import isEmpty from "../../common/helpers/isEmpty";
import { TalentLink } from "../orders/editOrder/long/talentsStatus/TalentLink";
import FeedbackFormRow from "./FeedbackFormRow";
import { ackCustomerFeedbackEntry } from "./FeedbacksSlice";
import { getShift } from "./helpers";
import { RelatesTo, Sentiment } from "./types/commands";
import { Feedback } from "./types/queries";
import { RelatesToKey } from "./types/types";
import { getName } from "../../common/helpers/common";

type Props = {
  open: boolean;
  onClose: () => void;
  feedback: Feedback;
};

const FeedbackManagementModal = ({ open, onClose, feedback }: Props) => {
  const { t } = useTranslation("feedbacks");

  const { workTitle } = useAppSelector((state) => state.localized);
  const { status } = useAppSelector((state) => state.feedbacks);

  const dispatch = useAppDispatch();
  const isLoading = status === "pending";

  const reporterFullName = getName(feedback?.reporter, "firstNameFirst");

  const defaultValues = useMemo(
    () => ({
      reporter: reporterFullName,
      workLocationName: feedback?.workLocationName || "",
      type: t(feedback.type),
      worktimeEntry: getShift(workTitle, feedback?.worktimeEntry),
      talent: getName(feedback?.talent),
      comment: feedback?.comment || "",
      createdAt: format(new Date(feedback.createdAt), leadingZerosDateFormat),
      sentiment: feedback?.sentiment || null,
      relatesTo: feedback?.relatesTo || null,
      ackByUserId: !!feedback?.ackByUserId, // boolean
    }),
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feedback, workTitle, reporterFullName, t, getName, format],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const onSave = (data: any) => {
    if (feedback.worktimeEntry?.id) {
      // TODO: after more feedback types are added, correct id should be checked by type
      dispatch(
        ackCustomerFeedbackEntry({
          id: feedback.worktimeEntry.id,
          ...(data?.sentiment && { sentiment: data?.sentiment }),
          ...(data?.relatesTo && { relatesTo: data?.relatesTo }),
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          onClose();
        }
      });
    }
  };

  const relatesToOptions = Object.keys(RelatesTo) as RelatesToKey[];

  // acknowledgedBy cannot be changed back to not acknowledged
  const saveDisabled =
    !!defaultValues.ackByUserId || !isEmpty(errors) || !isValid;

  return (
    <DialogComponent open={open} onClose={onClose} sx={{ maxWidth: "600px" }}>
      <DialogContentWrapper
        margin
        title={t("feedbackProcessing")}
        onCancel={onClose}
        onSave={handleSubmit(onSave)}
        saveDisabled={saveDisabled}
      >
        <OverlaySpinner isLoading={isLoading} />
        <FeedbackFormRow
          label={t("reporter")}
          name="reporter"
          control={control}
        />
        <FeedbackFormRow
          label={t("workLocationName")}
          name="workLocationName"
          control={control}
        />
        <FeedbackFormRow label={t("type")} name="type" control={control} />
        <FeedbackFormRow
          label={t("worktimeEntry")}
          name="worktimeEntry"
          control={control}
        />
        <FeedbackFormRow
          label={t("talent")}
          name="talent"
          component={
            <Controller
              name="talent"
              control={control}
              render={({ field }) => (
                <TalentLink
                  openInNewTab
                  style={{ textDecoration: "underline" }}
                  id={feedback.talent.id}
                  name={field.value}
                />
              )}
            />
          }
        />
        <FeedbackFormRow
          label={t("comment")}
          name="comment"
          control={control}
          GridLabelProps={{
            alignItems: "flex-start",
          }}
          GridFieldProps={{
            sx: { maxHeight: "360px", overflowY: "auto" },
          }}
        />
        <FeedbackFormRow
          label={t("relatesToOpt")}
          name="relatesTo"
          component={
            <Controller
              name="relatesTo"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  disabled={!!defaultValues.ackByUserId}
                  options={relatesToOptions}
                  value={field.value}
                  getOptionLabel={(option) => t(option)}
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(_, option) => field.onChange(option)}
                  renderInput={(params) => <CommonTextField {...params} />}
                />
              )}
            />
          }
        />
        <FeedbackFormRow
          label={t("sentimentOpt")}
          name="sentiment"
          GridLabelProps={{ textAlign: "right" }}
          component={
            <Controller
              name="sentiment"
              control={control}
              render={({ field }) => (
                <Box display="flex">
                  <IconButton
                    disabled={!!defaultValues.ackByUserId}
                    onClick={() => field.onChange(Sentiment.POSITIVE)}
                  >
                    <ThumbUpIcon
                      color={
                        field.value === Sentiment.POSITIVE
                          ? "primary"
                          : "inherit"
                      }
                    />
                  </IconButton>
                  <IconButton
                    disabled={!!defaultValues.ackByUserId}
                    onClick={() => field.onChange(Sentiment.NEGATIVE)}
                  >
                    <ThumbDownIcon
                      color={
                        field.value === Sentiment.NEGATIVE
                          ? "primary"
                          : "inherit"
                      }
                    />
                  </IconButton>
                </Box>
              )}
            />
          }
        />
        <FeedbackFormRow
          label={t("createdAt")}
          name="createdAt"
          control={control}
        />
        <Grid item xs={12}>
          <Divider sx={{ margin: "4px 0px" }} />
        </Grid>
        <FeedbackFormRow
          name="ackByUserId"
          component={
            <Controller
              name="ackByUserId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControlLabel
                  disabled={!!defaultValues.ackByUserId}
                  label={t("markAsManuallyProcessed")}
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e, checked) => field.onChange(checked)}
                    />
                  }
                />
              )}
            />
          }
        />
      </DialogContentWrapper>
    </DialogComponent>
  );
};

export default FeedbackManagementModal;
